import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Calendar, MapPin, Users, ArrowLeft, Globe2, CheckCircle2, XCircle, Ticket,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

function formatFull(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function EventoDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [evento, setEvento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [iscrizioneOk, setIscrizioneOk] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/api/eventi/${slug}`)
      .then((r) => setEvento(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  // Rileva ritorno da Stripe
  useEffect(() => {
    if (searchParams.get("iscrizione") === "ok") {
      setIscrizioneOk(true);
      toast({ description: "Iscrizione confermata! Controlla la tua email." });
    } else if (searchParams.get("iscrizione") === "annullata") {
      toast({
        description: "Pagamento annullato. Puoi riprovare in qualsiasi momento.",
        variant: "destructive",
      });
    }
  }, [searchParams, toast]);

  const gratis = evento && (!evento.prezzo_eur || evento.prezzo_eur <= 0);
  const postiEsauriti =
    evento &&
    evento.capienza_max > 0 &&
    evento.posti_disponibili !== null &&
    evento.posti_disponibili <= 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.cognome || !form.email) {
      toast({ description: "Compila i campi obbligatori.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        origin_url: window.location.origin,
      };
      const r = await axios.post(
        `${API}/api/eventi/${evento.id}/checkout`,
        payload,
        { withCredentials: true }
      );
      if (r.data.gratuita) {
        setIscrizioneOk(true);
        toast({ description: "Iscrizione confermata! Controlla la tua email." });
      } else if (r.data.url) {
        window.location.href = r.data.url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast({ description: `Errore: ${detail}`, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-24 text-center text-slate-500">
          Caricamento evento…
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !evento) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="Evento non trovato — A.C.D.T." />
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-24 text-center">
          <h1 className="text-3xl font-extrabold text-[#0a1f55]">Evento non trovato</h1>
          <Link to="/eventi">
            <Button className="mt-6 bg-[#0a1f55] hover:bg-[#102a6b]">
              <ArrowLeft className="mr-2 h-4 w-4" /> Torna al calendario
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: evento.titolo,
    description: evento.seo_description || evento.sottotitolo || "",
    startDate: evento.data_inizio,
    endDate: evento.data_fine || undefined,
    image: evento.cover_url || undefined,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode:
      evento.modalita === "online"
        ? "https://schema.org/OnlineEventAttendanceMode"
        : "https://schema.org/OfflineEventAttendanceMode",
    location:
      evento.modalita === "online"
        ? { "@type": "VirtualLocation", url: window.location.href }
        : {
            "@type": "Place",
            name: evento.luogo,
            address: evento.indirizzo || undefined,
          },
    organizer: {
      "@type": "Organization",
      name: "A.C.D.T.",
      url: "https://assoacdt.com",
    },
    offers: {
      "@type": "Offer",
      price: evento.prezzo_eur || 0,
      priceCurrency: "EUR",
      url: window.location.href,
      availability: postiEsauriti
        ? "https://schema.org/SoldOut"
        : "https://schema.org/InStock",
    },
  };

  return (
    <div className="relative min-h-screen bg-white" data-testid="evento-detail">
      <SEO
        title={evento.seo_title || `${evento.titolo} — A.C.D.T.`}
        description={evento.seo_description || evento.sottotitolo}
        image={evento.cover_url || undefined}
        jsonLd={jsonLd}
      />
      <Navbar />

      <section className="relative pt-14 pb-8 md:pt-16 md:pb-10 bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          {evento.cover_url && (
            <img
              src={evento.cover_url}
              alt=""
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div className="max-w-6xl mx-auto px-5 md:px-8 relative">
          <Link
            to="/eventi"
            className="inline-flex items-center gap-1.5 text-[#fbbf24] text-sm font-semibold hover:underline"
            data-testid="evento-back"
          >
            <ArrowLeft className="h-4 w-4" /> Calendario eventi
          </Link>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {evento.modalita === "online" && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-500 text-white text-[10.5px] font-extrabold uppercase tracking-widest">
                <Globe2 className="h-3 w-3" /> Online
              </span>
            )}
            {gratis && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10.5px] font-extrabold uppercase tracking-widest">
                Gratuito
              </span>
            )}
            {!gratis && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[10.5px] font-extrabold uppercase tracking-widest">
                € {evento.prezzo_eur}
              </span>
            )}
          </div>
          <h1
            data-testid="evento-title"
            className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight max-w-3xl"
          >
            {evento.titolo}
          </h1>
          {evento.sottotitolo && (
            <p className="mt-3 text-lg text-slate-200 max-w-3xl">{evento.sottotitolo}</p>
          )}
        </div>
      </section>

      <section className="relative py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-xl font-extrabold text-[#0a1f55] mb-3">Descrizione</h2>
              <p className="text-[15px] text-slate-700 leading-[1.75] whitespace-pre-wrap">
                {evento.descrizione}
              </p>
              {evento.tags && evento.tags.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {evento.tags.map((t) => (
                    <span
                      key={t}
                      className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-lg font-extrabold text-[#0a1f55] mb-4">Dettagli</h3>
              <ul className="space-y-3 text-[14.5px]">
                <li className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-[#f59e0b] mt-0.5" />
                  <div>
                    <div className="font-bold text-[#0a1f55]">Quando</div>
                    <div className="text-slate-600">{formatFull(evento.data_inizio)}</div>
                    {evento.data_fine && (
                      <div className="text-slate-500 text-xs">
                        Fine: {formatFull(evento.data_fine)}
                      </div>
                    )}
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#f59e0b] mt-0.5" />
                  <div>
                    <div className="font-bold text-[#0a1f55]">Dove</div>
                    <div className="text-slate-600">{evento.luogo}</div>
                    {evento.indirizzo && (
                      <div className="text-slate-500 text-xs">{evento.indirizzo}</div>
                    )}
                  </div>
                </li>
                {evento.capienza_max > 0 && (
                  <li className="flex items-start gap-3">
                    <Users className="h-5 w-5 text-[#f59e0b] mt-0.5" />
                    <div>
                      <div className="font-bold text-[#0a1f55]">Capienza</div>
                      <div className="text-slate-600">
                        {evento.posti_disponibili !== null
                          ? `${evento.posti_disponibili} / ${evento.capienza_max} posti disponibili`
                          : "Illimitata"}
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-lg">
                <div className="bg-gradient-to-br from-[#0a1f55] to-[#102a6b] text-white p-5">
                  <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#fbbf24]">
                    Iscrizione
                  </div>
                  <div className="mt-1 text-3xl font-extrabold">
                    {gratis ? "Gratuita" : `€ ${evento.prezzo_eur.toFixed(2)}`}
                  </div>
                  <p className="mt-1 text-xs text-slate-300">
                    {gratis ? "Nessun pagamento richiesto" : "Pagamento sicuro Stripe"}
                  </p>
                </div>

                {iscrizioneOk ? (
                  <div
                    className="p-5 text-center"
                    data-testid="evento-iscrizione-ok"
                  >
                    <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
                    <h3 className="text-lg font-extrabold text-[#0a1f55]">
                      Iscrizione confermata!
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">
                      Ti abbiamo inviato una email con tutti i dettagli.
                    </p>
                  </div>
                ) : postiEsauriti ? (
                  <div className="p-5 text-center">
                    <XCircle className="h-14 w-14 text-red-500 mx-auto mb-3" />
                    <h3 className="text-lg font-extrabold text-red-600">
                      Posti esauriti
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">
                      Questo evento ha raggiunto la capienza massima.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="p-5 space-y-3" data-testid="evento-form">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs font-bold text-[#0a1f55]">Nome *</Label>
                        <Input
                          value={form.nome}
                          onChange={(e) => setForm({ ...form, nome: e.target.value })}
                          required
                          data-testid="evento-form-nome"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-bold text-[#0a1f55]">Cognome *</Label>
                        <Input
                          value={form.cognome}
                          onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                          required
                          data-testid="evento-form-cognome"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-bold text-[#0a1f55]">Email *</Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        data-testid="evento-form-email"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold text-[#0a1f55]">Telefono</Label>
                      <Input
                        value={form.telefono}
                        onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                        data-testid="evento-form-telefono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold text-[#0a1f55]">Note (opz.)</Label>
                      <Textarea
                        rows={2}
                        value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        data-testid="evento-form-note"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="evento-form-submit"
                      className="w-full bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold py-6"
                    >
                      {submitting
                        ? "Attendere…"
                        : gratis
                        ? "Iscriviti gratis"
                        : `Paga € ${evento.prezzo_eur.toFixed(2)} con Stripe`}
                      <Ticket className="ml-2 h-4 w-4" />
                    </Button>
                    {!gratis && (
                      <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                        Verrai reindirizzato a Stripe Checkout. Al termine
                        riceverai la conferma via email.
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
